import { useCallback, useContext, useMemo, useRef, useState } from "react";
import { ColDef, SideBarDef } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { Button, Dropdown, Radio } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { Link, useParams } from "react-router-dom";
import CustomDateSetFilter from "entity-app/custom-filters/custom-date-filter";
import CiqAgSearch from "components/ciq-ag-search";
import {
  comparatorWithEmptyAtBottom,
  riskLevelComparator
} from "utils/grid-utils";
import { DATE_FORMAT_MMDDYYYY, DateUtils } from "utils/dateutils";
import { truncatedTextCellRenderer } from "components/cell-renders";
import { ProjectContext, TProjectContext } from "context/ProjectProvider";
import { MaterialImpactDetailCellRenderer } from "./impact-detail-renderer";
import { TMaterialGridRow, formatChangeInFloat } from "./grid-utils";
import { buildPrintPayloadHash } from "./optimized-print-view/print-payload";
import { launchPrintView } from "./optimized-print-view/print-view-launcher";
import { usePersistedExpansion } from "./use-persisted-expansion";
import {
  ALWAYS_EXPORT_KEYS_MATERIAL,
  SCHEDULE_IMPACT_EXPORT_COLUMN_KEYS_MATERIAL,
  SI_MATERIAL_RENAME_KEY,
  buildExportFileName,
  getVisibleExportColumnKeys,
  resolveSiHeader,
  scheduleImpactProcessCell
} from "./export-helpers";

type Props = {
  rowData: TMaterialGridRow[];
  isLoading: boolean;
  printMode?: boolean;
  importLogId?: string;
  sourceLogId?: string;
  sourceScheduleName?: string;
  importScheduleName?: string;
};

function materialIdInnerRenderer(params: any) {
  if (params.data.is_implicit) {
    return <span>{params.data.title}</span>;
  }
  const { projectId } = params.context || {};
  return (
    <Link to={`/project/${projectId}/materials/${params.data.material_id}`}>
      {params.value}
    </Link>
  );
}

function MaterialsNoRowsOverlay({ context }: any) {
  const { isLoading: loading } = context || {};
  return (
    <span>
      {loading ? (
        <span className="loading-dots">Loading</span>
      ) : (
        "No Materials are impacted."
      )}
    </span>
  );
}

function riskCellRenderer(params: any) {
  const level = params.value;
  if (!level) return "";
  const config: Record<string, { label: string; color: string }> = {
    HIGH: { label: "High", color: "#dc2626" },
    MEDIUM: { label: "Medium", color: "#d97706" },
    LOW: { label: "Low", color: "#16a34a" },
    DELETED: { label: "Deleted", color: "#6b7280" }
  };
  const { label, color } = config[level] || {
    label: level,
    color: "#6b7280"
  };
  return (
    <span style={{ color, fontWeight: 600, fontSize: "12px" }}>{label}</span>
  );
}

function dateValueFormatter(params: any) {
  if (!params.value) return "";
  return DateUtils.format(params.value, DATE_FORMAT_MMDDYYYY) || "";
}

const RISK_CATEGORIES = [
  { key: "ALL", label: "All" },
  { key: "HIGH", label: "Attention Required" },
  { key: "DELETED", label: "Deleted Activity" }
];

function ImpactedMaterialsGrid({
  rowData,
  isLoading,
  printMode,
  importLogId,
  sourceLogId,
  sourceScheduleName,
  importScheduleName
}: Props) {
  const gridRef = useRef<AgGridReact<any>>(null);
  const { projectId } = useParams() as any;
  const { projectDetails, columnHeaders, isProjectArchived }: TProjectContext =
    useContext(ProjectContext);
  const materialHeaderMap = columnHeaders?.materialHeaderMap;
  const { expandedItemIds, setExpandedItemIds, toggleExpanded, expandedRef } =
    usePersistedExpansion("materials", importLogId, sourceLogId);
  const [selectedCategory, setSelectedCategory] = useState<string>("ALL");

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = { ALL: rowData.length };
    rowData.forEach((r) => {
      counts[r.risk_level] = (counts[r.risk_level] || 0) + 1;
    });
    return counts;
  }, [rowData]);

  const filteredRowData = useMemo(
    () =>
      selectedCategory === "ALL"
        ? rowData
        : rowData.filter((r) => r.risk_level === selectedCategory),
    [rowData, selectedCategory]
  );

  const rowIds = useMemo(
    () => filteredRowData.map((r) => r.id),
    [filteredRowData]
  );

  const defaultColDef: ColDef = useMemo(
    () => ({
      sortable: true,
      editable: false,
      filter: true,
      resizable: true,
      menuTabs: ["filterMenuTab"] as any,
      sortingOrder: ["asc", "desc"] as any,
      comparator: comparatorWithEmptyAtBottom,
      tooltipValueGetter: (params: any) => params.value
    }),
    []
  );

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        colId: "material_sequence_id",
        field: "material_sequence_id",
        headerName: resolveSiHeader(
          materialHeaderMap,
          "ID",
          SI_MATERIAL_RENAME_KEY.material_sequence_id
        ),
        width: 100,
        pinned: "left",
        lockPinned: true,
        lockPosition: true,
        lockVisible: true,
        suppressMovable: true,
        cellRenderer: "agGroupCellRenderer",
        cellRendererParams: {
          innerRenderer: materialIdInnerRenderer
        }
      },
      {
        colId: "title",
        field: "title",
        headerName: resolveSiHeader(
          materialHeaderMap,
          "Material Title",
          SI_MATERIAL_RENAME_KEY.title
        ),
        minWidth: 200,
        flex: 1
      },
      {
        colId: "spec_section",
        field: "spec_section",
        headerName: resolveSiHeader(
          materialHeaderMap,
          "Spec No",
          SI_MATERIAL_RENAME_KEY.spec_section
        ),
        width: 120
      },
      {
        colId: "spec_name",
        field: "spec_name",
        headerName: "Spec Name",
        width: 160,
        cellRenderer: truncatedTextCellRenderer
      },
      {
        colId: "responsible_contractor",
        field: "responsible_contractor",
        headerName: resolveSiHeader(
          materialHeaderMap,
          "Responsible Contractor",
          SI_MATERIAL_RENAME_KEY.responsible_contractor
        ),
        width: 180,
        cellRenderer: truncatedTextCellRenderer
      },
      {
        colId: "gc_representative",
        field: "gc_representative",
        headerName: resolveSiHeader(
          materialHeaderMap,
          "GC Representative",
          SI_MATERIAL_RENAME_KEY.gc_representative
        ),
        width: 160,
        cellRenderer: truncatedTextCellRenderer,
        filter: "agSetColumnFilter"
      },
      {
        colId: "status",
        field: "status",
        headerName: resolveSiHeader(
          materialHeaderMap,
          "Status",
          SI_MATERIAL_RENAME_KEY.status
        ),
        width: 160,
        cellRenderer: truncatedTextCellRenderer
      },
      {
        colId: "linked_activity",
        field: "linked_activity",
        headerName: resolveSiHeader(
          materialHeaderMap,
          "Impacting Activity",
          SI_MATERIAL_RENAME_KEY.linked_activity
        ),
        width: 200,
        cellRenderer: truncatedTextCellRenderer
      },
      {
        colId: "change_in_float",
        field: "change_in_float",
        headerName: "Change in Float",
        width: 150,
        valueFormatter: ({ value }: { value: number | null | undefined }) =>
          formatChangeInFloat(value),
        // Numeric — default string sort would put "+10" before "+2".
        comparator: (a: number | null, b: number | null) =>
          (a ?? Number.NEGATIVE_INFINITY) - (b ?? Number.NEGATIVE_INFINITY)
      },
      {
        colId: "risk_before",
        field: "risk_before",
        headerName: "Before Risk",
        width: 140,
        cellRenderer: riskCellRenderer,
        filter: "agSetColumnFilter",
        comparator: riskLevelComparator
      },
      {
        colId: "risk_after",
        field: "risk_after",
        headerName: "After Risk",
        width: 140,
        cellRenderer: riskCellRenderer,
        filter: "agSetColumnFilter",
        sort: "asc",
        comparator: riskLevelComparator
      },
      {
        colId: "old_activity_date",
        headerName: "Old Activity Date",
        valueGetter: (p: any) => p.data?._activity?.old_start ?? "",
        hide: true,
        suppressColumnsToolPanel: true,
        suppressFiltersToolPanel: true
      },
      {
        colId: "new_activity_date",
        headerName: "New Activity Date",
        valueGetter: (p: any) => p.data?._activity?.new_start ?? "",
        hide: true,
        suppressColumnsToolPanel: true,
        suppressFiltersToolPanel: true
      },
      {
        colId: "delta",
        field: "delta",
        headerName: "Delta",
        hide: true,
        suppressColumnsToolPanel: true,
        suppressFiltersToolPanel: true
      },
      {
        colId: "risk_assessment",
        field: "risk_assessment",
        headerName: "Risk Assessment after Schedule Change",
        hide: true,
        suppressColumnsToolPanel: true,
        suppressFiltersToolPanel: true
      },
      {
        colId: "final_deadline",
        field: "final_deadline",
        headerName: resolveSiHeader(
          materialHeaderMap,
          "Final Deadline",
          SI_MATERIAL_RENAME_KEY.final_deadline
        ),
        headerTooltip: "As per New Schedule",
        width: 160,
        valueFormatter: dateValueFormatter,
        filter: CustomDateSetFilter
      }
    ],
    [materialHeaderMap]
  );

  const expandCollapseAll = useCallback(() => {
    let tmpExpandedItemIds: string[] = [];
    if (expandedItemIds.length && expandedItemIds.length === rowIds.length) {
      tmpExpandedItemIds = [];
    } else {
      tmpExpandedItemIds = rowIds;
    }

    gridRef?.current?.api?.forEachNode((node: any) => {
      node.expanded = !!tmpExpandedItemIds.length;
    });
    gridRef?.current?.api?.onGroupExpandedOrCollapsed();
    setExpandedItemIds(tmpExpandedItemIds);
  }, [expandedItemIds.length, rowIds, setExpandedItemIds]);

  const exportToExcel = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    api.exportDataAsExcel({
      fileName: buildExportFileName("impacted-materials", projectDetails?.name),
      sheetName: "Impacted Materials",
      processCellCallback: scheduleImpactProcessCell,
      columnKeys: getVisibleExportColumnKeys(
        api,
        SCHEDULE_IMPACT_EXPORT_COLUMN_KEYS_MATERIAL,
        ALWAYS_EXPORT_KEYS_MATERIAL
      ),
      skipColumnHeaders: false,
      skipRowGroups: true
    });
  }, [projectDetails?.name]);

  const openPrintView = useCallback(
    (autoprint = false) => {
      if (!importLogId || !sourceLogId || !projectId) return;
      const api = gridRef.current?.api;
      const filteredIds: string[] = [];
      let collected = false;
      if (api) {
        try {
          if (
            typeof (api as any).forEachNodeAfterFilterAndSort === "function"
          ) {
            (api as any).forEachNodeAfterFilterAndSort((node: any) => {
              if (node?.data?.id) filteredIds.push(node.data.id);
            });
            collected = true;
          } else if (typeof api.forEachNodeAfterFilter === "function") {
            api.forEachNodeAfterFilter((node: any) => {
              if (node?.data?.id) filteredIds.push(node.data.id);
            });
            collected = true;
          }
        } catch {
          collected = false;
        }
      }
      if (!collected) {
        filteredIds.push(...filteredRowData.map((r) => r.id));
      }
      // Capture the toggleable column ids that are currently visible so
      // the print view's table mirrors what the user sees in the grid.
      // Always-hidden columns (delta, risk_assessment, etc.) report
      // isVisible() === false and are naturally excluded.
      const visibleColumnIds =
        api
          ?.getColumns()
          ?.filter((c: any) => c.isVisible())
          .map((c: any) => c.getColId()) ?? undefined;
      const params: Record<string, string> = {
        importLogId,
        sourceLogId,
        tab: "materials"
      };
      if (autoprint) params.autoprint = "1";
      const qs = new URLSearchParams(params).toString();
      // Filter payload travels in the URL fragment so it survives any
      // storage isolation (incognito, strict privacy mode, partitioned
      // localStorage). Server never sees `#`-text.
      const hash = buildPrintPayloadHash({
        importLogId,
        sourceLogId,
        tab: "materials",
        filteredIds,
        sourceScheduleName,
        importScheduleName,
        visibleColumnIds
      });
      window.open(
        `/project/${projectId}/schedule/change-impact/printview?${qs}#${hash}`,
        "_blank",
        "noopener"
      );
    },
    [
      importLogId,
      sourceLogId,
      projectId,
      filteredRowData,
      sourceScheduleName,
      importScheduleName
    ]
  );

  const detailCellRenderer = useMemo(
    () => MaterialImpactDetailCellRenderer,
    []
  );

  const gridContext = useMemo(
    () => ({ projectId, isLoading }),
    [projectId, isLoading]
  );

  const getRowId = useCallback((params: any) => params.data.id, []);

  const handleFirstDataRendered = useCallback(
    (event: any) => {
      if (printMode) {
        event.api.forEachNode((node: any) => {
          node.setExpanded(true);
        });
        return;
      }
      const saved = expandedRef.current;
      if (saved.length === 0) return;
      const ids = new Set(saved);
      event.api.forEachNode((node: any) => {
        if (node?.data?.id && ids.has(node.data.id)) node.setExpanded(true);
      });
    },
    [printMode, expandedRef]
  );

  const handleRowGroupOpened = useCallback(
    (event: any) => {
      if (event?.data?.id) toggleExpanded(event.data.id);
    },
    [toggleExpanded]
  );

  const sideBar = useMemo<SideBarDef>(
    () => ({
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true
          }
        }
      ],
      defaultToolPanel: ""
    }),
    []
  );

  return (
    <div data-testid="impacted-materials-grid" className="flex flex-col">
      {!printMode && (
        <div className="pb-2 pt-6 px-4 flex items-center justify-between hide-on-print">
          <Radio.Group
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            {RISK_CATEGORIES.map((cat) => {
              const count = categoryCounts[cat.key] || 0;
              return (
                <Radio.Button key={cat.key} value={cat.key}>
                  <span className="inline-flex items-center gap-1.5">
                    {cat.label}
                    <span className="border border-solid border-ciq-gray-300 rounded-xl px-1.5 text-xs font-normal">
                      {count}
                    </span>
                  </span>
                </Radio.Button>
              );
            })}
          </Radio.Group>
          <div className="flex items-center gap-x-3">
            <CiqAgSearch
              key="ImpactedMaterials_search"
              gridRef={gridRef}
              placeholder="Search Materials"
            />
            <Button
              onClick={expandCollapseAll}
              className="w-[110px]"
              disabled={!filteredRowData.length}
            >
              {expandedItemIds.length &&
              expandedItemIds.length === rowIds.length ? (
                <span>Collapse All</span>
              ) : (
                <span>Expand All</span>
              )}
            </Button>
            <Dropdown
              placement="bottomRight"
              disabled={!filteredRowData.length || !!isProjectArchived}
              menu={{
                items: [
                  {
                    key: "excel",
                    label: (
                      <span data-testid="impacted-materials-excel">
                        Export as Excel
                      </span>
                    ),
                    onClick: exportToExcel
                  },
                  {
                    key: "pdf",
                    disabled: !importLogId || !sourceLogId,
                    label: (
                      <span data-testid="impacted-materials-pdf">
                        Export as PDF
                      </span>
                    ),
                    onClick: () => {
                      const api = gridRef.current?.api;
                      const visibleCount =
                        api?.getDisplayedRowCount?.() ?? filteredRowData.length;
                      launchPrintView(() => openPrintView(true), visibleCount);
                    }
                  }
                ]
              }}
            >
              <Button
                disabled={!filteredRowData.length || !!isProjectArchived}
                data-testid="impacted-materials-export"
              >
                Export <DownOutlined />
              </Button>
            </Dropdown>
          </div>
        </div>
      )}
      <div style={{ height: printMode ? "auto" : "calc(100vh - 166px)" }}>
        <AgGridReact
          ref={gridRef}
          className="ag-theme-alpine"
          defaultColDef={defaultColDef}
          columnDefs={columnDefs}
          rowData={filteredRowData}
          getRowId={getRowId}
          rowHeight={48}
          headerHeight={36}
          tooltipShowDelay={300}
          pagination={false}
          suppressPaginationPanel
          suppressContextMenu
          masterDetail
          detailCellRenderer={detailCellRenderer}
          detailRowAutoHeight
          detailRowHeight={500}
          keepDetailRows={!!printMode}
          context={gridContext}
          sideBar={printMode ? undefined : sideBar}
          domLayout={printMode ? "print" : "normal"}
          suppressRowVirtualisation={!!printMode}
          suppressColumnVirtualisation={!!printMode}
          suppressMaxRenderedRowRestriction={!!printMode}
          onFirstDataRendered={handleFirstDataRendered}
          onRowGroupOpened={handleRowGroupOpened}
          noRowsOverlayComponent={MaterialsNoRowsOverlay}
        />
      </div>
    </div>
  );
}

export default ImpactedMaterialsGrid;
